<template>
  <div>
    <div class="col-12 mb-4">
      <div class="d-flex bg-white shadow-lg p-3 rounded-3 justify-content-between">
        <h3 class="fw-bold">
          <vue-feather type="bar-chart-2" stroke="#652aa3" size="28" class="me-2"></vue-feather>
          {{ getTitle }}
        </h3>
      </div>
    </div>

    <div class="col-12 mb-4">
      <div class="card shadow-sm border-0">
        <div class="card-body bg-white rounded-3">
          <h5 class="card-title mb-3">
            <vue-feather type="filter" stroke="#652aa3" size="20" class="me-2"></vue-feather>
            Filters
          </h5>
          <div class="row g-3">
            <!-- Employee Filter -->
            <div class="col-md-4">
              <label class="form-label">Employee</label>
              <v-select
                  label="name"
                  v-model="filters.employee_id"
                  style="padding-top: 4px;"
                  :reduce="employee => employee.id"
                  :options="employees"
                  class="style-chooser"
                  placeholder="Type to search"
                  @option:selected="applyFilters"
              ></v-select>
            </div>

            <!-- Month From -->
            <div class="col-md-2">
              <label for="month-from" class="form-label">Month From</label>
              <select id="month-from" class="form-select shadow-sm" v-model="filters.month_from" @change="applyFilters" data="">
                <option value="" disabled>Select Month</option>
                <option v-for="(month, index) in months" :key="index" :value="month.value">{{ month.text }}</option>
              </select>
            </div>

            <!-- Year From -->
            <div class="col-md-2">
              <label for="year-from" class="form-label">Year From</label>
              <select id="year-from" class="form-select shadow-sm" v-model="filters.year_from" @change="applyFilters" data="">
                <option value="">Select Year</option>
                <option v-for="year in analytics.years" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>

            <!-- Month To -->
            <div class="col-md-2">
              <label for="month-to" class="form-label">Month To</label>
              <select id="month-to" class="form-select shadow-sm" v-model="filters.month_to" @change="applyFilters" data="">
                <option value="" disabled>Select Month</option>
                <option v-for="(month, index) in months" :key="index" :value="month.value">{{ month.text }}</option>
              </select>
            </div>

            <!-- Year To -->
            <div class="col-md-2">
              <label for="year-to" class="form-label">Year To</label>
              <select id="year-to" class="form-select shadow-sm" v-model="filters.year_to" @change="applyFilters" data="">
                <option value="">Select Year</option>
                <option v-for="year in analytics.years" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="row mb-4">
          <!-- Chart Area -->
          <div>
            <div v-if="loading" class="text-center py-4">
              <Spinner spinner-class="mb-1" />
            </div>
            <div v-else>
              <!-- Monthly Gross Bar Chart -->
              <GrossBarChart
                  :title="grossChartTitle"
                  :data="this.analytics"
              ></GrossBarChart>

            <!-- Monthly Deductions Bar Chart -->
              <DeductionsBarChart
                  :data="this.analytics"
                  :title="deductionsChartTitle"
              >
              </DeductionsBarChart>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import analyticsService from "@/api/analyticsService";
import GrossBarChart from "@/components/charts/GrossBarChart.vue";
import formatNumber from "@/utils/formatNumber";
import DeductionsBarChart from "@/components/charts/DeductionsBarChart.vue";
import VueFeather from 'vue-feather';
import selectSearch from "@/utils/selectSearch";

export default {
  name: "MonthlyReport",
  components: {DeductionsBarChart, GrossBarChart, VueFeather },

  data() {
    return {
      loading: true,
      analytics: {
        years: [],
        data: {
          months: [],
          grossDataset: [],
          deductionDatasets: []
        }
      },
      employees: [],
      months: [
        {'value': '01', 'text': 'January'},
        {'value': '02', 'text': 'February'},
        {'value': '03', 'text': 'March'},
        {'value': '04', 'text': 'April'},
        {'value': '05', 'text': 'May'},
        {'value': '06', 'text': 'June'},
        {'value': '07', 'text': 'July'},
        {'value': '08', 'text': 'August'},
        {'value': '09', 'text': 'September'},
        {'value': '10', 'text': 'October'},
        {'value': '11', 'text': 'November'},
        {'value': '12', 'text': 'December'},
      ],
      filters: {
        employee_id: '',
        month_from: '01',
        year_from: new Date().getFullYear(),
        month_to: this.currentMonth(),
        year_to: new Date().getFullYear(),
      }
    }
  },

  methods: {
    formatNumber,
    fetchEmployees: async function() {
      this.employees = await selectSearch.allEmployees()
    },
    currentMonth: function() {
      const date = new Date();
      let month = date.getMonth() + 1;
      return month.toString().padStart(2, '0');
    },
    applyFilters: async function() {
      this.loading = true;
      let filters = {
        employee_id: this.filters.employee_id,
        from: `${this.filters.year_from}-${this.filters.month_from}-01`,
        to: `${this.filters.year_to}-${this.filters.month_to}-01`,
      }
      this.analytics = await analyticsService.getMonthlyAnalytics(filters)
      this.loading = false;
    }
  },

  created: async function () {
    await Promise.all([
      this.applyFilters(),
      this.fetchEmployees(),
    ]);
  },

  computed: {
    filterRange() {
      let employee = this.employees.find(e => e.id === this.filters.employee_id)
      let name = employee ? employee.name : 'All Employees'
      let monthFrom = this.months.find(m => m.value === this.filters.month_from)
      let monthTo = this.months.find(m => m.value === this.filters.month_to)
      let yearFrom = this.filters.year_from
      let yearTo = this.filters.year_to
      return `${name} (${monthFrom.text} ${yearFrom} to ${monthTo.text} ${yearTo})`
    },
    getTitle() {
      return 'Report Summary: ' + this.filterRange
    },
    grossChartTitle() {
      return 'Payroll Summary: ' + this.filterRange
    },
    deductionsChartTitle() {
      return 'Deductions Summary: ' + this.filterRange
    }
  }
}
</script>

<style scoped>
.table th, .table td {
  text-align: right;
}
.table th:first-child, .table td:first-child {
  text-align: left;
}

.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.total-card {
  min-width: 230px;
  opacity: 70%;
  transition: all 0.3s ease;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.total-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.text-primary {
  color: #652aa3 !important;
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

/* Filter styles */
.form-select, .form-control {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.form-select:focus, .form-control:focus {
  border-color: #652aa3;
  box-shadow: 0 0 0 0.25rem rgba(101, 42, 163, 0.25);
}

.form-label {
  font-weight: 500;
  color: #495057;
  margin-bottom: 0.5rem;
}
</style>
